/**
 * Licence DETAIL PAGE (ADR-0073 licence overhaul) — routed at `/platform/licenses/:id`.
 *
 * The licence used to live entirely in a table row plus two cramped dialogs, so the three
 * questions the vendor actually asks — *what does this customer have, until when, and what
 * is their install doing* — each needed a different popup and none of them showed the
 * entitlements at all. This is the tenant-detail layout applied to the other kind of
 * deployment: a header with the identity + state, and tabs that split validity, what was
 * sold, and the emission ledger.
 *
 * It reads the licence out of the LIST (`/licenses`) rather than a per-id endpoint: the
 * on-prem fleet is a handful of rows and the list is already the page's own cache, so a
 * new route would have bought a second source of truth for nothing.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Activity,
  ArrowLeft,
  Building2,
  History,
  KeySquare,
  Loader2,
  ShieldCheck,
  ShieldOff,
  TriangleAlert,
} from "lucide-react";
import * as React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { EntitlementsSummary } from "@/components/platform/EntitlementsEditor";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { PageContainer, PageContent } from "@/components/ui/page";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { daysSinceReport, reportCounts, reportVersion } from "@/lib/licenseReport";
import {
  type PlanEntitlements,
  type PlatformLicense,
  PlatformApiError,
  platformApi,
} from "@/lib/platform";

import { LicenseIssuancesPanel } from "./LicenseIssuancesDialog";
import { OrganizationLinkDialog } from "./OrganizationLinkDialog";

const LICENSES_KEY = ["platform", "licenses"];

const PERIOD_LABEL: Record<string, string> = {
  monthly: "Mensile",
  annual: "Annuale",
  custom: "Personalizzato",
};

function fmtDate(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleDateString() : "—";
}

function fmtDateTime(iso: string | null | undefined): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

/** The licence's state, said the way the operator would say it. */
function StatusBadge({ lic }: { lic: PlatformLicense }) {
  if (lic.status === "revoked") return <Badge tone="danger">Revocata</Badge>;
  if (!lic.expires_at) return <Badge tone="warning">Nessuna emissione</Badge>;
  if (new Date(lic.expires_at).getTime() < Date.now())
    return <Badge tone="warning">Scaduta</Badge>;
  if (!lic.activated_at) return <Badge tone="draft">Da attivare</Badge>;
  return <Badge tone="active">Attiva</Badge>;
}

export function PlatformLicenseDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [linking, setLinking] = React.useState(false);

  const listQ = useQuery({ queryKey: LICENSES_KEY, queryFn: () => platformApi.listLicenses() });
  const lic = (listQ.data ?? []).find((l) => l.id === id) ?? null;

  const catalogQ = useQuery({
    queryKey: ["platform", "entitlements-catalog"],
    queryFn: () => platformApi.entitlementsCatalog(),
  });

  const revoke = useMutation({
    mutationFn: () => platformApi.revokeLicense(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: LICENSES_KEY }),
  });

  const back = (
    <button
      type="button"
      onClick={() => navigate("..")}
      className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
      Licenze
    </button>
  );

  if (listQ.isLoading) {
    return (
      <PageContainer>
        <div className="px-4 pt-5 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Caricamento…
          </div>
        </div>
      </PageContainer>
    );
  }
  if (listQ.isError || !lic) {
    return (
      <PageContainer>
        <div className="px-4 pt-5 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-6 flex items-start gap-2 text-sm text-danger">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
            {listQ.error instanceof PlatformApiError
              ? listQ.error.message
              : "Licenza non trovata."}
          </div>
        </div>
      </PageContainer>
    );
  }

  const issuance = lic.current_issuance;

  return (
    <PageContainer>
      <Tabs defaultValue="overview" orientation="vertical" className="flex h-full min-h-0 flex-col">
        <div className="shrink-0 border-b border-border/60 px-4 pt-5 pb-4 sm:px-8 sm:pt-6">
          {back}
          <div className="mt-3 flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
            <div className="flex min-w-0 items-start gap-3">
              <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border/60 bg-surface text-muted-foreground">
                <KeySquare className="h-5 w-5" strokeWidth={1.5} />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <h1 className="truncate text-xl font-semibold tracking-tight text-foreground">
                    {lic.organization}
                  </h1>
                  <StatusBadge lic={lic} />
                </div>
                <p className="mt-1 truncate font-mono text-xs text-muted-foreground">
                  {lic.activation_key_prefix}… · emessa {fmtDate(lic.created_at)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setLinking(true)}>
                <Building2 className="h-4 w-4" strokeWidth={1.5} />
                Organizzazione
              </Button>
              {lic.status !== "revoked" && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-danger"
                  disabled={revoke.isPending}
                  onClick={() => {
                    if (
                      window.confirm(
                        `Revocare la licenza di "${lic.organization}"? L'installazione andrà in modalità ristretta al prossimo heartbeat.`,
                      )
                    )
                      revoke.mutate();
                  }}
                >
                  <ShieldOff className="h-4 w-4" strokeWidth={1.5} />
                  Revoca
                </Button>
              )}
            </div>
          </div>
        </div>

        <div className="flex min-h-0 flex-1 flex-col md:flex-row">
          <div className="shrink-0 border-b border-border/60 p-3 md:w-56 md:border-b-0 md:border-r">
            <TabsList className="gap-0.5 bg-transparent">
              <TabsTrigger value="overview">
                <Activity className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                Panoramica
              </TabsTrigger>
              <TabsTrigger value="entitlements">
                <ShieldCheck className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                Cosa include
              </TabsTrigger>
              <TabsTrigger value="issuances">
                <History className="h-4 w-4 shrink-0" strokeWidth={1.5} />
                Emissioni
              </TabsTrigger>
            </TabsList>
          </div>

          <PageContent className="pt-6">
            <TabsContent value="overview" className="grid gap-4 lg:grid-cols-2">
              <Card title="Validità" icon={Activity}>
                <dl className="space-y-2 text-sm">
                  <Row
                    label="Finestra"
                    value={
                      issuance
                        ? `${fmtDate(issuance.starts_at)} → ${fmtDate(issuance.ends_at)}`
                        : "Nessuna emissione"
                    }
                  />
                  <Row
                    label="Periodo"
                    value={
                      issuance
                        ? (PERIOD_LABEL[issuance.billing_period] ?? issuance.billing_period)
                        : "—"
                    }
                  />
                  <Row label="Giorni di grazia" value={String(lic.grace_days)} />
                  <Row label="Emissioni" value={String(lic.issuance_count)} />
                </dl>
              </Card>
              <Card title="Installazione" icon={KeySquare}>
                <dl className="space-y-2 text-sm">
                  <Row label="Attivata" value={fmtDateTime(lic.activated_at)} />
                  <Row label="Ultimo heartbeat" value={fmtDateTime(lic.heartbeat_at)} />
                  <Row label="ID installazione" value={lic.installation_id || "—"} mono />
                  <Row label="Versione" value={reportVersion(lic.last_report) || "—"} mono />
                </dl>
                {/* An install that has never reported says so — a fabricated "0 workflow"
                    would read as a real (and alarming) number (ADR-0074 L1). */}
                <InstallCounts lic={lic} />
              </Card>
            </TabsContent>

            <TabsContent value="entitlements">
              <Card title="Cosa include questa licenza" icon={ShieldCheck}>
                <p className="mb-4 text-xs text-muted-foreground">
                  Fotografia scattata all&apos;emissione in vigore: modificare il piano non cambia
                  una licenza già emessa. Per cambiare ciò che è incluso, emetti una modifica dalla
                  scheda Emissioni.
                </p>
                {catalogQ.data ? (
                  <EntitlementsSummary
                    catalog={catalogQ.data}
                    value={
                      (issuance?.entitlements ?? lic.entitlements ?? {}) as unknown as PlanEntitlements
                    }
                  />
                ) : (
                  <p className="text-sm text-muted-foreground">Caricamento…</p>
                )}
              </Card>
            </TabsContent>

            <TabsContent value="issuances">
              <LicenseIssuancesPanel license={lic} />
            </TabsContent>
          </PageContent>
        </div>
      </Tabs>

      {linking && (
        <OrganizationLinkDialog
          target={{ kind: "license", id: lic.id, label: lic.organization }}
          currentOrganizationId={lic.organization_id}
          onClose={() => setLinking(false)}
        />
      )}
    </PageContainer>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={mono ? "font-mono text-xs" : "text-foreground"}>{value}</dd>
    </div>
  );
}

function InstallCounts({ lic }: { lic: PlatformLicense }) {
  const counts = reportCounts(lic.last_report);
  const days = daysSinceReport(lic);
  if (counts.length === 0)
    return (
      <p className="mt-3 text-xs text-muted-foreground">
        Questa installazione non ha mai inviato un report.
      </p>
    );
  return (
    <div className="mt-3 border-t border-border/60 pt-3">
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        {counts.map((c) => (
          <span key={c.label} className="tabular-nums">
            <span className="font-medium">{c.value.toLocaleString()}</span>{" "}
            <span className="text-muted-foreground">{c.label}</span>
          </span>
        ))}
      </div>
      {days !== null && (
        <p className="mt-1 text-xs text-muted-foreground">
          Report di {days === 0 ? "oggi" : `${days} giorni fa`}.
        </p>
      )}
    </div>
  );
}

function Card({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon?: typeof Activity;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-border/60 bg-surface/40">
      <div className="flex items-center gap-2 border-b border-border/60 px-4 py-2.5">
        {Icon && <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />}
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}
